import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";
import React from "react";
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

interface ModalProps {
  title?: string;
  activeTab?: string;
  onTabChange?: (value: string) => void;
  children?: ReactNode;
  onDismiss: () => void;
  visible: boolean;
  offsetTop?: number;
  anchorRef?: RefObject<HTMLElement>;
  containerRef: RefObject<HTMLDivElement>;
  className?: string;
}

const ModalContainer: React.FC<ModalProps> = ({
  title,
  offsetTop,
  children,
  onDismiss,
  visible,
  anchorRef,
  className,
}) => {
  const [computedTop, setComputedTop] = useState(null);
  const [computedLeft, setComputedLeft] = useState(null);
  const minTop = 48;
  const maxOffsetFromBottom = 48;
  const leftPadding = 16;
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current?.contains(event.target as Node)) {
        return;
      }
      let node = event.target as HTMLElement | null;
      while (node) {
        if (
          node.getAttribute &&
          node.getAttribute("data-inside-modal") === "true"
        ) {
          return;
        }
        node = node.parentElement;
      }
      onDismiss();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismiss();
      }
    };

    const calculateTopOffset = () => {
      if (anchorRef?.current && modalRef.current) {
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const portalRoot = document.getElementById("styling-panel-portal-root");
        const container = portalRoot?.parentElement;

        if (container) {
          const containerRect = container.getBoundingClientRect();
          const containerHeight = containerRect.height;
          const modalHeight = modalRef.current.offsetHeight;
          const modalWidth = modalRef.current.offsetWidth;

          // Position modal to the left of the right most edge of the container
          const left = containerRect.right - modalWidth;

          const maxTop = containerHeight - modalHeight - maxOffsetFromBottom;
          let top = anchorRect.top;

          // Apply min and max constraints relative to the container
          top = Math.max(top, minTop);
          top = Math.min(top, maxTop);

          // @ts-expect-error TS2345
          setComputedTop(top);
          // @ts-expect-error TS2345
          setComputedLeft(left - leftPadding);
        } else {
          setComputedTop(null);
          setComputedLeft(null);
        }
      }
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      calculateTopOffset();

      // Watch for size changes and recalculate position immediately
      let resizeObserver: ResizeObserver | null = null;
      if (modalRef.current) {
        resizeObserver = new ResizeObserver(() => {
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(calculateTopOffset);
        });
        resizeObserver.observe(modalRef.current);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleKeyDown);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [visible, onDismiss, anchorRef]);

  if (!visible) {
    return null;
  }

  const top = computedTop || 48;
  return ReactDOM.createPortal(
    <div
      className={cn(
        "fixed z-100000 w-[400px] bg-secondary-0 rounded-md p-2 border-secondary-500 border text-xs flex flex-col gap-y-8",
        className,
      )}
      style={{
        top: `${top}px`,
        left: `${computedLeft}px`,
      }}
      ref={modalRef}
    >
      <div className={cn("flex flex-col", title ? "gap-y-2" : "gap-y-0")}>
        <div
          className={cn(
            "flex flex-row items-center justify-between",
            !title && "h-0",
          )}
        >
          {title && (
            <div className="text-xs font-semibold text-white">{title}</div>
          )}
          <XIcon
            className={cn(
              "cursor-pointer text-secondary-600 hover:text-secondary-800 w-4 h-4 shrink-0",
              !title && "absolute top-4 right-4",
            )}
            onClick={onDismiss}
          />
        </div>
        {children}
      </div>
    </div>,
    // @ts-expect-error TS2345
    document.getElementById("styling-panel-portal-root"),
  );
};

export default React.memo(ModalContainer);
